import { isOpenLinearWorkflowTailKind } from "./workflow-stage-kind";
import type { TWorkflowElement } from "@/types/form-workflow";

/**
 * Pure resolution of a workflow step drag-and-drop (NG-694) — the logic half of
 * canvas step reordering, kept free of React and of the store so it is unit
 * testable and so the drag hook can ask "would this drop be legal?" (to light a
 * slot up) with the exact predicate the store commits through.
 *
 * A DROP SLOT is one of the canvas "+" insertion points: the connector edges
 * ``layoutWorkflow`` emits already sit at every legal insertion position, so the
 * drag reuses that vocabulary rather than inventing drop geometry. The two
 * flavours mirror ``WorkflowAddEdgeData``:
 *  - ``after``     — the gap following ``afterId`` (an inter-step connector, or
 *                    the trailing append connector after a lane's last step);
 *  - ``lane-head`` — the gap at the head of a Review option / Branch true-false
 *                    lane, before its first step (or the whole of an empty lane).
 * Connector-only edges (past a terminal End, and the parent→pill lane entry)
 * carry no "+" and are therefore never drop slots.
 */
export type TWorkflowDropSlot =
  | { kind: "after"; afterId: string }
  | { kind: "lane-head"; parentId: string; branchKey: string };

/**
 * Stable string identity for a slot. Two uses, one definition: it keys the drop
 * target's effect (a slot object is rebuilt every render, so its identity is
 * useless as a dependency) and it labels the slot in the DOM for an e2e drag to
 * aim at.
 */
export function workflowDropSlotKey(slot: TWorkflowDropSlot): string {
  return slot.kind === "after"
    ? `after:${slot.afterId}`
    : `lane:${slot.parentId}:${slot.branchKey}`;
}

/** Where a resolved drop puts the dragged step: an index within the destination
 *  lane, counted over that lane WITHOUT the dragged element. */
export interface TWorkflowDropTarget {
  parentId: string | null;
  branchKey: string | null;
  index: number;
}

const laneOf = (
  elements: TWorkflowElement[],
  parentId: string | null,
  branchKey: string | null,
): TWorkflowElement[] =>
  elements.filter((el) => el.parent_id === parentId && el.branch_key === branchKey);

/** True when *candidateId* is *rootId* or descends from it via ``parent_id``.
 *  Guards the "a step cannot move under itself" rule; the ``seen`` set keeps a
 *  cyclic payload from hanging the walk. */
const isWithinSubtree = (
  elements: TWorkflowElement[],
  candidateId: string,
  rootId: string,
): boolean => {
  const byId = new Map(elements.map((el) => [el.id, el]));
  let node = byId.get(candidateId);
  const seen = new Set<string>();
  while (node && !seen.has(node.id)) {
    if (node.id === rootId) return true;
    seen.add(node.id);
    node = node.parent_id ? byId.get(node.parent_id) : undefined;
  }
  return false;
};

/**
 * Whether *id* is a step a user may pick up at all — the fixed first Page is
 * pinned by the PRD (§6.1.4) and never moves. Exported so the card can withhold
 * the grip handle rather than offering a drag every slot would then refuse.
 */
export function canDragWorkflowStep(elements: TWorkflowElement[], id: string): boolean {
  const element = elements.find((el) => el.id === id);
  return element ? !isPinnedFirstPage(element) : false;
}

/** The fixed first Page, which no move may relocate (PRD §6.1.4). */
const isPinnedFirstPage = (element: TWorkflowElement): boolean =>
  element.kind === "page" && Boolean(element.page?.is_first);

/**
 * Resolve a drop of *draggedId* onto *slot* into the destination lane + index,
 * or ``null`` when the drop is illegal or a no-op.
 *
 * Rejected — each one mirrors a rule the store already enforces for the
 * "Move to" menu (``moveElementToLane``), so drag and menu cannot disagree:
 *  - the fixed first Page never moves;
 *  - a step cannot land inside its own subtree (it would orphan itself);
 *  - a step that terminates its lane (an End, and equally a Review or Branch,
 *    which continue through their own lanes) may only land LAST;
 *  - nothing may land after such a step, which would strand it on a path no
 *    request can reach;
 *  - the two gaps flanking the step's current position leave it where it is.
 *
 * INVARIANT — ``index`` is relative to the destination lane with the dragged
 * element REMOVED. Callers that splice must remove before inserting, which is
 * what makes the flanking-gap no-op arithmetic below correct.
 */
export function resolveWorkflowDrop(
  elements: TWorkflowElement[],
  draggedId: string,
  slot: TWorkflowDropSlot,
): TWorkflowDropTarget | null {
  const dragged = elements.find((el) => el.id === draggedId);
  if (!dragged) return null;

  let parentId: string | null;
  let branchKey: string | null;
  let index: number;

  if (slot.kind === "after") {
    if (slot.afterId === draggedId) return null;
    const anchor = elements.find((el) => el.id === slot.afterId);
    if (!anchor) return null;
    parentId = anchor.parent_id;
    branchKey = anchor.branch_key;
    const without = laneOf(elements, parentId, branchKey).filter((el) => el.id !== draggedId);
    const anchorIndex = without.findIndex((el) => el.id === anchor.id);
    if (anchorIndex < 0) return null;
    index = anchorIndex + 1;
  } else {
    parentId = slot.parentId;
    branchKey = slot.branchKey;
    index = 0;
  }

  return validateMove(elements, dragged, parentId, branchKey, index);
}

/**
 * Resolve a "Move to <lane>" menu pick — the alternate path the kebab offers —
 * against the SAME rules as a drop, so the two cannot disagree about what a
 * legal move is.
 *
 * The menu's position is the lane's tail: after everything, but BEFORE a
 * trailing End, since that End terminates the lane. Where that position is not
 * legal for the moved step — a Review cannot sit in front of an End, because
 * everything after it routes through its own lanes — this returns ``null`` and
 * the lane is not offered, rather than the pick silently doing nothing.
 */
export function resolveWorkflowLaneMove(
  elements: TWorkflowElement[],
  draggedId: string,
  parentId: string | null,
  branchKey: string | null,
): TWorkflowDropTarget | null {
  const dragged = elements.find((el) => el.id === draggedId);
  if (!dragged) return null;
  // The menu relocates ACROSS lanes; its own lane is not an offer.
  if (dragged.parent_id === parentId && dragged.branch_key === branchKey) return null;
  const without = laneOf(elements, parentId, branchKey).filter((el) => el.id !== draggedId);
  const endsWithEnd = without.length > 0 && without[without.length - 1].kind === "end";
  const index = endsWithEnd ? without.length - 1 : without.length;
  return validateMove(elements, dragged, parentId, branchKey, index);
}

/**
 * The rules every move obeys, wherever it came from. Returns the target, or
 * ``null`` when the move is illegal or would change nothing.
 */
function validateMove(
  elements: TWorkflowElement[],
  dragged: TWorkflowElement,
  parentId: string | null,
  branchKey: string | null,
  index: number,
): TWorkflowDropTarget | null {
  const draggedId = dragged.id;
  if (isPinnedFirstPage(dragged)) return null;
  if (index < 0) return null;
  if (parentId !== null && isWithinSubtree(elements, parentId, draggedId)) return null;

  const without = laneOf(elements, parentId, branchKey).filter((el) => el.id !== draggedId);
  if (index > without.length) return null;

  // Lane terminality (NG-694). Only a Page / Change Owner carries a linear
  // successor and so leaves its lane open — a Review or Branch continues through
  // its OWN lanes and an End is terminal, per
  // {@link isOpenLinearWorkflowTailKind}. Two rules fall out of that, and both
  // are needed: dropping a Review mid-chain stranded every step behind it on a
  // path a request can never reach again (the bug this comment exists for).
  const predecessor = index > 0 ? without[index - 1] : null;
  // 1. Nothing may land AFTER a step that terminates its lane.
  if (predecessor && !isOpenLinearWorkflowTailKind(predecessor.kind)) return null;
  // 2. A step that terminates its lane must land LAST, with nothing behind it.
  //    This also subsumes "one End per lane": a second End could only go at the
  //    tail, where rule 1 then rejects it for following the first.
  if (!isOpenLinearWorkflowTailKind(dragged.kind) && index !== without.length) return null;

  const sameLane = dragged.parent_id === parentId && dragged.branch_key === branchKey;
  if (sameLane) {
    const currentIndex = laneOf(elements, parentId, branchKey).findIndex(
      (el) => el.id === draggedId,
    );
    // Re-inserting at its own index restores the original order — the gaps either
    // side of the step are no-ops, not moves.
    if (index === currentIndex) return null;
  }

  return { parentId, branchKey, index };
}
